/* eslint-disable react-hooks/static-components */
import { Icon, Image, ImageFit } from '@fluentui/react';
import { forwardRef, HTMLAttributes, ReactElement, Ref } from 'react';

import { getSafeExternalHref } from '@shared/lib/utils';

import { IComponent } from '../../models/components.models';
import { Sizes } from '../../themes/styles';
import { useClassNames, useTheme } from '../../utils/hooks';
import { Item } from '../Item/Item';
import { itemStylesCategory } from '../Item/Item.styles';
import { loaderStylesSpinnerTag } from '../Loader/Loader.styles';
import { IWidgetItemClassNames, IWidgetItemStyles, widgetItemStylesDefault } from './WidgetItem.styles';

type OnClickFn<T, E> = (data: T, e: React.MouseEvent<E>) => void;

export interface IWidgetItemProps<T extends { [key: string]: any }>
  extends IComponent<IWidgetItemStyles>,
    Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  data: T;
  idField: keyof T;
  titleField: keyof T;
  statusField?: keyof T;
  statusColorField?: keyof T;
  statusLoadingField?: keyof T;
  statusTextColorField?: keyof T;
  descriptionField?: keyof T;
  imagePathField?: keyof T;
  imageBackgroundColorField?: keyof T;
  iconNameField?: keyof T;
  disabledField?: keyof T;
  selected?: boolean;
  actions?: JSX.Element;
  last?: boolean;
  onClick?: OnClickFn<T, HTMLButtonElement>;
  onIconClick?: OnClickFn<T, HTMLDivElement>;
  onImageClick?: OnClickFn<T, HTMLDivElement>;
  onTitleClick?: OnClickFn<T, HTMLDivElement | HTMLAnchorElement>;
  iconHref?: string;
  imageHref?: string;
  titleHref?: string;
}

function getElementType(hrefProp?: string) {
  return hrefProp ? 'a' : 'div';
}

export const WidgetItem = forwardRef(
  <T extends { [key: string]: any }>(props: IWidgetItemProps<T>, ref: Ref<HTMLDivElement>) => {
    const {
        styles,
        className,
        data,
        idField = 'id' as keyof T,
        titleField = 'title' as keyof T,
        descriptionField,
        iconNameField,
        imageBackgroundColorField,
        imagePathField,
        statusField,
        statusColorField,
        statusLoadingField,
        statusTextColorField,
        disabledField,
        selected,
        actions,
        last,
        onClick,
        onIconClick,
        onImageClick,
        onTitleClick,
        iconHref,
        imageHref,
        titleHref,
        ...elementProps
      } = props,
      isDisabled = disabledField ? !!data[disabledField] : false,
      theme = useTheme(),
      classNames = useClassNames<Partial<IWidgetItemStyles>, IWidgetItemClassNames>(
        'WidgetItem',
        widgetItemStylesDefault,
        styles,
        className,
        selected && 'selected',
        !!onClick && 'clickable-content',
        last && 'last',
        isDisabled && 'disabled'
      ),
      safeImageHref = getSafeExternalHref(imageHref),
      safeIconHref = getSafeExternalHref(iconHref),
      safeTitleHref = getSafeExternalHref(titleHref),
      WidgetContainerElement = onClick ? 'button' : 'div',
      ImageContainerElement = getElementType(safeImageHref),
      IconContainerElement = getElementType(safeIconHref),
      TitleContainerElement = getElementType(safeTitleHref);

    return (
      <div
        {...elementProps}
        ref={ref}
        key={`${data[idField]}`}
        className={classNames.root}
        data-test={props['data-test'] || 'widget-item'}
      >
        <WidgetContainerElement
          className={classNames.content}
          disabled={WidgetContainerElement === 'button' ? isDisabled : undefined}
          onClick={
            onClick
              ? (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) =>
                  onClick(data, event as React.MouseEvent<HTMLButtonElement>)
              : undefined
          }
        >
          {imagePathField && (
            <ImageContainerElement
              href={safeImageHref}
              onClick={
                onImageClick
                  ? (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) =>
                      onImageClick(data, event as React.MouseEvent<HTMLDivElement>)
                  : undefined
              }
            >
              <Image
                alt="Item logo"
                src={data[imagePathField] ? data[imagePathField] : 'logo512.png'}
                imageFit={ImageFit.centerContain}
                styles={{
                  root: {
                    background: imageBackgroundColorField
                      ? data[imageBackgroundColorField]
                      : theme.palette?.transparent,
                    height: 40,
                    width: 40,
                    flexShrink: 0,
                    marginRight: 8,
                    borderRadius: Sizes.borderRadius,
                  },
                }}
              />
            </ImageContainerElement>
          )}
          {iconNameField && (
            <IconContainerElement
              href={safeIconHref}
              onClick={
                onIconClick
                  ? (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) =>
                      onIconClick(data, event as React.MouseEvent<HTMLDivElement>)
                  : undefined
              }
            >
              <div
                className={classNames.icon}
                onClick={
                  onIconClick
                    ? (e) => {
                        onIconClick(data, e);
                        e.stopPropagation();
                      }
                    : undefined
                }
              >
                {data[iconNameField] && <Icon iconName={data[iconNameField]} />}
              </div>
            </IconContainerElement>
          )}
          <div className={classNames.body}>
            <TitleContainerElement
              className={classNames.title}
              role={onTitleClick ? 'button' : ''}
              href={safeTitleHref}
              onClick={
                onTitleClick
                  ? (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
                      onTitleClick(data, e);
                      e.stopPropagation();
                    }
                  : undefined
              }
            >
              {`${data[titleField]}`}
            </TitleContainerElement>
            <div className={classNames.description}>
              {statusField && (
                <Item
                  labelField="label"
                  styles={[
                    itemStylesCategory,
                    {
                      root: {
                        background: statusColorField ? data[statusColorField] : theme.palette?.green200,
                        color: statusTextColorField ? data[statusTextColorField] : theme.semanticColors?.textPrimary,
                      },
                    },
                  ]}
                  loading={statusLoadingField ? data[statusLoadingField] : false}
                  loaderProps={
                    statusLoadingField
                      ? {
                          label: data[statusField],
                          styles: loaderStylesSpinnerTag,
                        }
                      : undefined
                  }
                  data={{ label: data[statusField] }}
                />
              )}
              {descriptionField && <div className={classNames.descriptionText}>{`${data[descriptionField]}`}</div>}
            </div>
          </div>
        </WidgetContainerElement>
        {actions && <div className={classNames.actions}>{actions}</div>}
      </div>
    );
  }
) as <T extends { [key: string]: any }>(props: IWidgetItemProps<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement;
